import { useMutation, useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import { generatePath } from 'react-router-dom'

import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { BLAZE_HOME_PATH } from 'paths'

interface UpdateSubscriptionParams {
  paymentMethodId?: string
  priceId?: string
  reactivate?: boolean
}

interface UpdateSubscriptionOptions {
  onSuccess?: () => void
  onError?: (error: any) => void
  closeAllModals?: () => void
  removeSettingsModalQueryParam?: () => void
  skipRedirect?: boolean
}

export const useUpdateAutopilotSubscription = (options?: UpdateSubscriptionOptions) => {
  const workspace = useCurrentWorkspace()
  const { tierUsageStore, creditUsageStore } = useRootStore()
  const queryClient = useQueryClient()
  const history = useHistory()

  const mutation = useMutation(
    async ({ paymentMethodId, priceId, reactivate }: UpdateSubscriptionParams) => {
      if (!workspace) {
        throw new Error('No workspace found')
      }

      const response = await sendToApi(`/w/${workspace.id}/virtual_marketers/update_subscription`, {
        body: {
          payment_method_id: paymentMethodId,
          price_id: priceId,
          reactivate,
        },
        method: 'PUT',
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to update subscription')
      }

      return response
    },
    {
      onMutate: () => {
        const dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
        return { dismiss }
      },
      onSuccess: async (data, variables, context) => {
        context?.dismiss()

        if (workspace) {
          await Promise.all([
            tierUsageStore.refetch(workspace.id),
            creditUsageStore.fetch(workspace.id),
            WorkspaceService.getWorkspaces(),
          ])
          queryClient.invalidateQueries(['can_create_additional_workspace'])
        }

        showToastMessage('Subscription updated successfully!')

        options?.closeAllModals?.()
        options?.removeSettingsModalQueryParam?.()

        if (workspace && !options?.skipRedirect) {
          history.push(generatePath(BLAZE_HOME_PATH, { workspaceId: workspace.id }))
        }

        options?.onSuccess?.()
      },
      onError: (error, variables, context) => {
        context?.dismiss()
        showToastMessage('An error occurred. Try again. Contact support if the issue persists.', 'error')
        options?.onError?.(error)
      },
    },
  )

  return {
    updateSubscription: mutation.mutate,
    updateSubscriptionAsync: mutation.mutateAsync,
    isUpdating: mutation.isLoading,
    error: mutation.error,
    reset: mutation.reset,
  }
}
